import type { PriceDetailsProps } from '@naturehouse/ui-library/components/01-molecules/PriceDetails/PriceDetails';
import type { PriceListCollectionApiData } from '../../../assets/js/collections/price-list/PriceListCollection.types';
import TranslationManager from '../../../assets/js/common/TranslationManager';
import ExtraCostPaymentType from '../../../assets/js/enums';

export default class PriceDetailsModel {
    private static data: PriceListCollectionApiData;

    public static async toComponentProps(
        data: PriceListCollectionApiData
    ): Promise<Omit<PriceDetailsProps, 'showPayUponArrivalAsterisk'>> {
        this.data = data;

        const { bookingInformation, priceInformation } = this.data;
        return {
            numberOfNights: bookingInformation.numberOfNights,
            rentalPrice: priceInformation.basePrice,
            serviceFee: priceInformation.serviceFee,
            totalPrice: priceInformation.total.discount,
            ...this.getDiscountPrice(),
            ...this.getInsuranceCosts(),
            ...this.getDeposit(),
            ...(await this.getIncludedCosts()),
            ...(await this.getFormattedCosts())
        };
    }

    private static getDiscountPrice(): Partial<PriceDetailsProps> {
        const { priceInformation } = this.data;

        return {
            discountPrice: priceInformation.discount ? priceInformation.discount : undefined
        };
    }

    private static getInsuranceCosts(): Partial<PriceDetailsProps> {
        const { priceInformation } = this.data;

        return {
            insuranceCosts: priceInformation.insurance.enabled
                ? priceInformation.insurance.value
                : undefined
        };
    }

    private static getDeposit(): Partial<PriceDetailsProps> {
        const { priceInformation } = this.data;

        return {
            depositType: priceInformation.deposit.type ? priceInformation.deposit.type : undefined,
            depositPrice: priceInformation.deposit.value
                ? priceInformation.deposit.value
                : undefined
        };
    }

    private static async getIncludedCosts(): Promise<Partial<PriceDetailsProps>> {
        const { priceInformation } = this.data;

        const translationManager = await TranslationManager.getInstance();
        const includedCosts = priceInformation.includedCosts.map((includedCost) =>
            translationManager.translate(includedCost)
        );

        return {
            includedCosts
        };
    }

    private static async getFormattedCosts(): Promise<Partial<PriceDetailsProps>> {
        const extraCosts = this.data.priceInformation.extraCosts;
        const translationManager = await TranslationManager.getInstance();
        const excludedCosts = [];
        const afterStayCosts = [];

        const touristTax = this.data.priceInformation.touristTax;
        if (touristTax != null && touristTax > 0) {
            excludedCosts.push({
                label: translationManager.translate('touristTax.label'),
                price: touristTax,
                paymentType: ExtraCostPaymentType.REQUIRED_ON_BOOKING
            });
        }

        for (const extraCost of extraCosts) {
            const price =
                extraCost.totalPrice !== null &&
                extraCost.paymentType !== ExtraCostPaymentType.AFTER_STAY_COSTS
                    ? extraCost.totalPrice
                    : extraCost.price;
            if (!extraCost.selected || price <= 0) {
                continue;
            }

            const formattedCost = {
                label: translationManager.translate(extraCost.name),
                price: price,
                unit: translationManager.translate(extraCost.priceType),
                paymentType: extraCost.paymentType
            };

            switch (extraCost.paymentType) {
                case ExtraCostPaymentType.AFTER_STAY_COSTS:
                    afterStayCosts.push(formattedCost);
                    continue;
                default:
                    excludedCosts.push(formattedCost);
                    continue;
            }
        }

        return {
            excludedCosts,
            afterStayCosts
        };
    }
}
